import React from 'react';
import { motion } from 'framer-motion';
import Navigation from '../components/navigation';
import { Footer } from '../components/footer';
import { Hammer, Home, Trash2, CheckCircle2, ArrowRight, Shield, Clock, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import { services } from '../data/mock';
import { useLanguage } from '../context/languagecontext';

const iconMap = {
  Hammer: Hammer,
  Home: Home,
  Trash2: Trash2
};

const ServicesPage = () => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      <div className="relative bg-slate-900 text-white py-24 mt-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/photo_5116493174661647892_y.jpg"
            alt="Services"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="font-heading text-5xl md:text-6xl font-bold mb-6 uppercase tracking-wide">
              {t('services.title')}
            </h1>
            <p className="font-body text-xl text-gray-300 max-w-3xl mx-auto">
              {t('services.subtitle')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12"
          >
            {[
              { icon: Shield, text: 'Fully Insured & WCB' },
              { icon: Award, text: 'Code Compliant' },
              { icon: Clock, text: 'On-Time Delivery' }
            ].map((badge, index) => (
              <div key={index} className="flex items-center justify-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-sm px-6 py-3">
                <badge.icon className="w-5 h-5 text-orange-500" />
                <span className="font-body font-bold uppercase tracking-wider text-sm">{badge.text}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="space-y-24">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            const isEven = index % 2 === 0;
            
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:grid-flow-dense' : ''}`}
              >
                <div className={`${!isEven ? 'lg:col-start-2' : ''}`}>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.3 }}
                    className="relative rounded-sm overflow-hidden shadow-lg border-2 border-slate-200 hover:border-orange-500 transition-colors group"
                  >
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-96 object-cover group-hover:scale-110 transition-transform duration-700 grayscale-[30%] group-hover:grayscale-0"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                    <div className="absolute top-6 left-6 w-20 h-20 bg-orange-500 rounded-sm flex items-center justify-center shadow-hard">
                      <IconComponent className="w-10 h-10 text-white" strokeWidth={1.5} />
                    </div>
                    <div className="absolute bottom-6 right-6 w-16 h-16 bg-white rounded-sm flex items-center justify-center shadow-hard-sm">
                      <span className="font-heading text-3xl font-bold text-slate-900">0{service.id}</span>
                    </div>
                  </motion.div>
                </div>

                <motion.div
                  initial={{ opacity: 0, x: isEven ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className={`${!isEven ? 'lg:col-start-1 lg:row-start-1' : ''}`}
                >
                  <div className="inline-block bg-orange-500/10 border border-orange-500/30 rounded-sm px-4 py-2 mb-4">
                    <span className="font-body text-orange-500 text-sm font-bold uppercase tracking-wider">
                      Service {service.id}
                    </span>
                  </div>
                  
                  <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-6 uppercase tracking-wide">
                    {t(`services.service${service.id}.title`)}
                  </h2>
                  <p className="font-body text-lg text-gray-600 mb-8 leading-relaxed">
                    {t(`services.service${service.id}.description`)}
                  </p>

                  <div className="space-y-4 mb-8">
                    {service.features.map((feature, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: idx * 0.1 }}
                        className="flex items-start space-x-3 group"
                      >
                        <div className="w-6 h-6 bg-orange-500 rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:shadow-hard-sm transition-shadow">
                          <CheckCircle2 className="w-4 h-4 text-white" strokeWidth={3} />
                        </div>
                        <span className="font-body text-gray-700 leading-relaxed">
                          {t(`services.service${service.id}.feature${idx + 1}`)}
                        </span>
                      </motion.div>
                    ))}
                  </div>

                  <Link
                    to="/contact"
                    className="inline-flex items-center px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wider rounded-sm shadow-hard transition-all active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
                  >
                    {t('services.cta')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </motion.div>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mt-24 bg-gradient-to-br from-slate-900 to-slate-800 rounded-sm p-12 text-center text-white shadow-2xl border-2 border-orange-500 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl"></div>
          <div className="relative z-10">
            <h2 className="font-heading text-4xl font-bold mb-4 uppercase tracking-wide">
              Need Something Custom?
            </h2>
            <p className="font-body text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Every project is unique. Contact us for custom concrete cutting and construction solutions tailored to your specific needs.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wider rounded-sm shadow-hard transition-all active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"
            >
              Get Custom Quote
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default ServicesPage;
